import React, { useEffect, useMemo, useRef, useState } from "react";
import { Download, UserPlus, RefreshCw, X, CheckCircle2, Upload, Mic2 } from "lucide-react";

const ADMIN_PASSWORD = 'Unicorn2025!';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TICKET_LABELS = { creator: 'Creator', host: 'Host' };
const SOURCE_LABELS = { stripe: 'Stripe', manual: 'Manual', eventbrite: 'Eventbrite' };
const INTEREST_LABELS = { speaker: 'Speaking', sponsor: 'Sponsoring', both: 'Both' };

const EVENTS = [
    { year: 2026, label: 'Summit 2026', subtitle: 'Breckenridge, CO · September 27–29, 2026 · Mountain Thunder Lodge' },
    { year: 2027, label: 'Summit 2027 · Early Access', subtitle: 'Breckenridge, CO · September 24–26, 2027 · Early access $399 (reg. $699) · Refundable until November 30, 2026' },
];

const EMPTY_FORM = {
    name: '',
    email: '',
    ticketType: 'creator',
    propertyName: '',
    socialHandle: '',
    speakerInterest: false,
    speakerTopic: '',
};

const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

const formatAmount = (cents) => {
    if (cents == null) return '—';
    return `$${(cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    })}`;
};

const csvEscape = (value) => {
    const str = String(value ?? '');
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const downloadCsv = (filename, header, rows) => {
    const csv = [header, ...rows]
        .map((row) => row.map(csvEscape).join(','))
        .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

// Minimal RFC-4180-ish CSV parser (quotes, escaped quotes, CRLF). Eventbrite
// exports are well-formed so this is plenty.
const parseCsv = (text) => {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    const src = String(text || '').replace(/^\uFEFF/, '');
    for (let i = 0; i < src.length; i += 1) {
        const ch = src[i];
        if (inQuotes) {
            if (ch === '"') {
                if (src[i + 1] === '"') { field += '"'; i += 1; }
                else inQuotes = false;
            } else {
                field += ch;
            }
        } else if (ch === '"') {
            inQuotes = true;
        } else if (ch === ',') {
            row.push(field); field = '';
        } else if (ch === '\n' || ch === '\r') {
            if (ch === '\r' && src[i + 1] === '\n') i += 1;
            row.push(field); field = '';
            if (row.some((c) => c.trim() !== '')) rows.push(row);
            row = [];
        } else {
            field += ch;
        }
    }
    row.push(field);
    if (row.some((c) => c.trim() !== '')) rows.push(row);
    return rows;
};

// Map arbitrary headers (Eventbrite, Google Sheets, hand-made) onto the
// fields we import. Returns { name, email, ticketType } per row.
const mapCsvRows = (rows) => {
    if (!rows.length) return { rows: [], columns: {} };
    const header = rows[0].map((h) => String(h || '').trim().toLowerCase());
    const find = (...candidates) => {
        for (const c of candidates) {
            const idx = header.findIndex((h) => h === c);
            if (idx !== -1) return idx;
        }
        for (const c of candidates) {
            const idx = header.findIndex((h) => h.includes(c));
            if (idx !== -1) return idx;
        }
        return -1;
    };
    const emailIdx = find('email', 'e-mail', 'email address');
    const nameIdx = find('name', 'attendee name', 'full name');
    const firstIdx = find('first name', 'first');
    const lastIdx = find('last name', 'last', 'surname');
    const ticketIdx = find('ticket type', 'ticket', 'type', 'track');
    const propertyIdx = find('property', 'property name', 'company');
    const socialIdx = find('social', 'instagram', 'handle');

    const hasHeader = emailIdx !== -1;
    const dataRows = hasHeader ? rows.slice(1) : rows;

    const mapped = dataRows.map((r) => {
        if (!hasHeader) {
            // No header: assume name, email[, ticket type]
            return { name: r[0] || '', email: r[1] || '', ticketType: r[2] || '' };
        }
        const first = firstIdx !== -1 ? r[firstIdx] : '';
        const last = lastIdx !== -1 ? r[lastIdx] : '';
        const name =
            (nameIdx !== -1 && nameIdx !== firstIdx && nameIdx !== lastIdx && r[nameIdx]) ||
            [first, last].filter(Boolean).join(' ').trim();
        return {
            name: String(name || '').trim(),
            email: String(r[emailIdx] || '').trim(),
            ticketType: ticketIdx !== -1 ? String(r[ticketIdx] || '').trim() : '',
            propertyName: propertyIdx !== -1 ? String(r[propertyIdx] || '').trim() : '',
            socialHandle: socialIdx !== -1 ? String(r[socialIdx] || '').trim() : '',
        };
    }).filter((r) => r.email || r.name);

    return {
        rows: mapped,
        columns: { hasHeader, email: emailIdx, name: nameIdx, first: firstIdx, last: lastIdx, ticket: ticketIdx },
    };
};

const postAdmin = async (payload) => {
    const res = await fetch('/api/admin/summit-attendees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminPassword: ADMIN_PASSWORD, ...payload }),
    });
    return res.json();
};

const SummitAttendees = () => {
    const [allAttendees, setAllAttendees] = useState([]);
    const [allInterest, setAllInterest] = useState([]);
    const [eventYear, setEventYear] = useState(2027);
    const [view, setView] = useState('attendees'); // attendees | interest
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const activeEvent = EVENTS.find((e) => e.year === eventYear) || EVENTS[0];
    const attendees = useMemo(
        () => allAttendees.filter((a) => (a.eventYear || 2026) === eventYear),
        [allAttendees, eventYear]
    );
    const countsByYear = useMemo(() => {
        const map = {};
        allAttendees.forEach((a) => {
            const y = a.eventYear || 2026;
            map[y] = (map[y] || 0) + 1;
        });
        return map;
    }, [allAttendees]);

    // Speakers & Sponsors = dedicated interest-form submissions + ticket
    // holders who ticked "interested in speaking" at checkout, in one list.
    const interestRows = useMemo(() => {
        const fromForm = allInterest
            .filter((i) => (i.eventYear || 2027) === eventYear)
            .map((i) => ({
                id: `interest-${i.id}`,
                name: i.name,
                email: i.email,
                interestType: i.interestType,
                socialHandle: i.socialHandle,
                topic: i.topic,
                source: 'form',
                createdAt: i.createdAt,
            }));
        const fromTickets = attendees
            .filter((a) => a.speakerInterest)
            .map((a) => ({
                id: `ticket-${a.id}`,
                name: a.name,
                email: a.email,
                interestType: 'speaker',
                socialHandle: a.socialHandle,
                topic: a.speakerTopic,
                source: 'ticket',
                ticketType: a.ticketType,
                createdAt: a.createdAt,
            }));
        return [...fromForm, ...fromTickets].sort(
            (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        );
    }, [allInterest, attendees, eventYear]);

    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [added, setAdded] = useState(null);

    // CSV import
    const fileInputRef = useRef(null);
    const [importOpen, setImportOpen] = useState(false);
    const [importRows, setImportRows] = useState([]);
    const [importFileName, setImportFileName] = useState('');
    const [importPreview, setImportPreview] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState('');

    const fetchAll = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const [list, interest] = await Promise.all([
                postAdmin({ action: 'list' }),
                postAdmin({ action: 'listInterest' }),
            ]);
            if (list.ok) setAllAttendees(list.attendees || []);
            else setLoadError('Failed to load attendees. Please try again.');
            if (interest.ok) setAllInterest(interest.interest || []);
        } catch (err) {
            console.error('Error fetching summit data:', err);
            setLoadError('Failed to load attendees. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const counts = useMemo(() => {
        const creators = attendees.filter((a) => a.ticketType === 'creator').length;
        const hosts = attendees.filter((a) => a.ticketType === 'host').length;
        const speakers = interestRows.length;
        const revenueCents = attendees.reduce((sum, a) => sum + (a.amountCents || 0), 0);
        return { total: attendees.length, creators, hosts, speakers, revenueCents };
    }, [attendees, interestRows]);

    const handleExportCsv = () => {
        if (view === 'interest') {
            const header = ['Name', 'Interested In', 'Email', 'Social Handle', 'Topic / Offer', 'Source', 'Date'];
            const rows = interestRows.map((r) => [
                r.name,
                INTEREST_LABELS[r.interestType] || r.interestType,
                r.email,
                r.socialHandle ? `@${r.socialHandle}` : '',
                r.topic || '',
                r.source === 'ticket' ? `Ticket (${TICKET_LABELS[r.ticketType] || r.ticketType})` : 'Interest form',
                r.createdAt ? new Date(r.createdAt).toISOString().slice(0, 10) : '',
            ]);
            downloadCsv(`summit-${eventYear}-speakers-sponsors-${new Date().toISOString().slice(0, 10)}.csv`, header, rows);
            return;
        }
        const header = ['Name', 'Ticket Type', 'Email', 'Property Name', 'Social Handle', 'Amount Paid', 'Price Tier', 'Speaker Interest', 'Speaking Topic', 'Source', 'Date'];
        const rows = attendees.map((a) => [
            a.name,
            TICKET_LABELS[a.ticketType] || a.ticketType,
            a.email,
            a.propertyName || '',
            a.socialHandle ? `@${a.socialHandle}` : '',
            a.amountCents != null ? (a.amountCents / 100).toFixed(2) : '',
            a.priceTier === 'early_bird' ? 'Early Access' : a.priceTier === 'eventbrite' ? 'Eventbrite' : 'Standard',
            a.speakerInterest ? 'Yes' : 'No',
            a.speakerTopic || '',
            SOURCE_LABELS[a.source] || a.source,
            a.createdAt ? new Date(a.createdAt).toISOString().slice(0, 10) : '',
        ]);
        downloadCsv(`summit-${eventYear}-attendees-${new Date().toISOString().slice(0, 10)}.csv`, header, rows);
    };

    const openAddModal = () => {
        setForm(EMPTY_FORM);
        setFormError('');
        setAdded(null);
        setModalOpen(true);
    };

    const closeAddModal = () => {
        if (saving) return;
        setModalOpen(false);
    };

    const handleAddAttendee = async (e) => {
        e.preventDefault();
        setFormError('');

        const name = form.name.trim();
        const email = form.email.trim();
        if (!name) {
            setFormError('Name is required.');
            return;
        }
        if (!EMAIL_REGEX.test(email)) {
            setFormError('Enter a valid email address.');
            return;
        }

        setSaving(true);
        try {
            const data = await postAdmin({
                action: 'add',
                name,
                email,
                ticketType: form.ticketType,
                propertyName: form.propertyName.trim(),
                socialHandle: form.socialHandle.trim(),
                eventYear,
                speakerInterest: form.speakerInterest,
                speakerTopic: form.speakerInterest ? form.speakerTopic.trim() : '',
            });
            if (data.ok && data.attendee) {
                setAllAttendees((prev) => [data.attendee, ...prev]);
                setForm(EMPTY_FORM);
                setAdded(data.attendee);
            } else {
                setFormError('Failed to add attendee. Please try again.');
            }
        } catch (err) {
            console.error('Error adding summit attendee:', err);
            setFormError('Failed to add attendee. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const setField = (field) => (e) => {
        const value = e.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    // ---- CSV import ------------------------------------------------------
    const openImport = () => {
        setImportRows([]);
        setImportFileName('');
        setImportPreview(null);
        setImportResult(null);
        setImportError('');
        setImportOpen(true);
    };

    const closeImport = () => {
        if (importing) return;
        setImportOpen(false);
    };

    const handleFile = async (file) => {
        if (!file) return;
        setImportError('');
        setImportResult(null);
        setImportPreview(null);
        try {
            const text = await file.text();
            const { rows } = mapCsvRows(parseCsv(text));
            if (!rows.length) {
                setImportError("Couldn't find any rows with an email address in that file.");
                return;
            }
            setImportRows(rows);
            setImportFileName(file.name);
            setImporting(true);
            const data = await postAdmin({ action: 'import', eventYear, dryRun: true, rows });
            if (data.ok) setImportPreview(data);
            else setImportError('Preview failed. Check the file and try again.');
        } catch (err) {
            console.error('CSV import preview failed:', err);
            setImportError('Preview failed. Check the file and try again.');
        } finally {
            setImporting(false);
        }
    };

    const commitImport = async () => {
        if (!importRows.length) return;
        setImporting(true);
        setImportError('');
        try {
            const data = await postAdmin({ action: 'import', eventYear, dryRun: false, rows: importRows });
            if (data.ok) {
                setImportResult(data);
                if (data.attendees?.length) {
                    setAllAttendees((prev) => [...data.attendees, ...prev]);
                }
            } else {
                setImportError('Import failed. Nothing was added.');
            }
        } catch (err) {
            console.error('CSV import failed:', err);
            setImportError('Import failed. Nothing was added.');
        } finally {
            setImporting(false);
        }
    };

    return (
        <>
            <div className="admin-section">
                <div className="sa-header">
                    <div>
                        <h2>Hospitality Summit</h2>
                        <p className="sa-subtitle">{activeEvent.subtitle}</p>
                    </div>
                    <div className="sa-header-actions">
                        <button
                            type="button"
                            className="sa-btn sa-btn-secondary"
                            onClick={fetchAll}
                            disabled={loading}
                        >
                            <RefreshCw size={14} />
                            Refresh
                        </button>
                        <button
                            type="button"
                            className="sa-btn sa-btn-secondary"
                            onClick={handleExportCsv}
                            disabled={loading || (view === 'interest' ? interestRows.length === 0 : attendees.length === 0)}
                        >
                            <Download size={14} />
                            Export CSV
                        </button>
                        {view === 'attendees' && (
                            <>
                                <button
                                    type="button"
                                    className="sa-btn sa-btn-secondary"
                                    onClick={openImport}
                                    title="Import attendees from an Eventbrite (or any) CSV"
                                >
                                    <Upload size={14} />
                                    Import CSV
                                </button>
                                <button
                                    type="button"
                                    className="sa-btn sa-btn-primary"
                                    onClick={openAddModal}
                                >
                                    <UserPlus size={14} />
                                    Add Attendee
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div className="sa-event-tabs" role="tablist" aria-label="Summit event">
                    {EVENTS.map((ev) => (
                        <button
                            key={ev.year}
                            type="button"
                            role="tab"
                            aria-selected={ev.year === eventYear}
                            className={`sa-event-tab${ev.year === eventYear ? ' active' : ''}`}
                            onClick={() => setEventYear(ev.year)}
                        >
                            {ev.label}
                            <span className="sa-event-tab-count">{countsByYear[ev.year] || 0}</span>
                        </button>
                    ))}
                </div>

                <div className="sa-view-toggle" role="tablist" aria-label="View">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={view === 'attendees'}
                        className={`sa-view-btn${view === 'attendees' ? ' active' : ''}`}
                        onClick={() => setView('attendees')}
                    >
                        <UserPlus size={14} />
                        Attendees
                        <span className="sa-view-count">{attendees.length}</span>
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={view === 'interest'}
                        className={`sa-view-btn${view === 'interest' ? ' active' : ''}`}
                        onClick={() => setView('interest')}
                    >
                        <Mic2 size={14} />
                        Speakers &amp; Sponsors
                        <span className="sa-view-count">{interestRows.length}</span>
                    </button>
                </div>

                <div className={`sa-stats${eventYear === 2027 ? ' sa-stats-5' : ''}`}>
                    <div className="sa-stat">
                        <span className="sa-stat-value">{counts.total}</span>
                        <span className="sa-stat-label">Total Attendees</span>
                    </div>
                    <div className="sa-stat">
                        <span className="sa-stat-value">{counts.creators}</span>
                        <span className="sa-stat-label">Creators</span>
                    </div>
                    <div className="sa-stat">
                        <span className="sa-stat-value">{counts.hosts}</span>
                        <span className="sa-stat-label">Hosts</span>
                    </div>
                    {eventYear === 2027 && (
                        <div className="sa-stat">
                            <span className="sa-stat-value">{counts.speakers}</span>
                            <span className="sa-stat-label">Speakers &amp; Sponsors</span>
                        </div>
                    )}
                    <div className="sa-stat">
                        <span className="sa-stat-value">{formatAmount(counts.revenueCents)}</span>
                        <span className="sa-stat-label">Ticket Revenue</span>
                    </div>
                </div>

                {loadError && <div className="admin-error">{loadError}</div>}

                {loading ? (
                    <p className="sa-empty">Loading…</p>
                ) : view === 'interest' ? (
                    interestRows.length === 0 ? (
                        <p className="sa-empty">
                            No speaker or sponsor interest for {activeEvent.label} yet. Submissions from the
                            “Speaker or Sponsor” form on the summit page — and ticket buyers who tick “interested in speaking” — appear here.
                        </p>
                    ) : (
                        <div className="ca-table-wrap">
                            <table className="ca-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Interested In</th>
                                        <th>Email</th>
                                        <th>Social</th>
                                        <th>Topic / Offer</th>
                                        <th>Source</th>
                                        <th>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {interestRows.map((r) => (
                                        <tr key={r.id} className="ca-row" style={{ cursor: 'default' }}>
                                            <td className="sa-cell-name">{r.name}</td>
                                            <td>
                                                <span className={`sa-badge sa-badge-interest-${r.interestType}`}>
                                                    {INTEREST_LABELS[r.interestType] || r.interestType}
                                                </span>
                                            </td>
                                            <td>
                                                <a className="sa-email" href={`mailto:${r.email}`}>{r.email}</a>
                                            </td>
                                            <td>{r.socialHandle ? `@${r.socialHandle}` : '—'}</td>
                                            <td className="sa-cell-topic">
                                                {r.topic ? (
                                                    <div className="sa-speaker-topic sa-speaker-topic-wide" title={r.topic}>{r.topic}</div>
                                                ) : '—'}
                                            </td>
                                            <td>
                                                {r.source === 'ticket' ? (
                                                    <span className={`sa-badge sa-badge-${r.ticketType}`}>
                                                        {TICKET_LABELS[r.ticketType] || 'Ticket'} ticket
                                                    </span>
                                                ) : (
                                                    <span className="sa-badge sa-badge-form">Interest form</span>
                                                )}
                                            </td>
                                            <td>{formatDate(r.createdAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                ) : attendees.length === 0 ? (
                    <p className="sa-empty">No {activeEvent.label} attendees yet. Ticket purchases will appear here automatically.</p>
                ) : (
                    <div className="ca-table-wrap">
                        <table className="ca-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Type</th>
                                    <th>Email</th>
                                    <th>Property</th>
                                    <th>Social</th>
                                    {eventYear === 2027 && <th>Speaker?</th>}
                                    <th>Paid</th>
                                    <th>Source</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attendees.map((a) => (
                                    <tr key={a.id} className="ca-row" style={{ cursor: 'default' }}>
                                        <td className="sa-cell-name">{a.name}</td>
                                        <td>
                                            <span className={`sa-badge sa-badge-${a.ticketType}`}>
                                                {TICKET_LABELS[a.ticketType] || a.ticketType}
                                            </span>
                                            {a.priceTier === 'early_bird' && (
                                                <span className="sa-badge sa-badge-early-bird">Early Access</span>
                                            )}
                                        </td>
                                        <td>
                                            <a className="sa-email" href={`mailto:${a.email}`}>{a.email}</a>
                                        </td>
                                        <td>{a.propertyName || '—'}</td>
                                        <td>{a.socialHandle ? `@${a.socialHandle}` : '—'}</td>
                                        {eventYear === 2027 && (
                                            <td className="sa-cell-speaker">
                                                {a.speakerInterest ? (
                                                    <>
                                                        <span className="sa-badge sa-badge-speaker">Yes</span>
                                                        {a.speakerTopic && (
                                                            <div className="sa-speaker-topic" title={a.speakerTopic}>
                                                                {a.speakerTopic}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                        )}
                                        <td>{formatAmount(a.amountCents)}</td>
                                        <td>
                                            <span className={`sa-badge sa-badge-${a.source}`}>
                                                {SOURCE_LABELS[a.source] || a.source}
                                            </span>
                                        </td>
                                        <td>{formatDate(a.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {modalOpen && (
                <div className="sa-modal-overlay" onClick={closeAddModal}>
                    <div
                        className="sa-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="sa-modal-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="sa-modal-close"
                            onClick={closeAddModal}
                            disabled={saving}
                            aria-label="Close"
                        >
                            <X size={18} />
                        </button>

                        {added ? (
                            <div className="sa-modal-success">
                                <CheckCircle2 size={44} className="sa-modal-success-icon" />
                                <h3 id="sa-modal-title">Attendee added</h3>
                                <p>
                                    <strong>{added.name}</strong> (
                                    {TICKET_LABELS[added.ticketType] || added.ticketType}) has been
                                    added to the attendee list. No confirmation email was sent.
                                </p>
                                <div className="sa-modal-actions">
                                    <button
                                        type="button"
                                        className="sa-btn sa-btn-secondary"
                                        onClick={openAddModal}
                                    >
                                        <UserPlus size={14} />
                                        Add Another
                                    </button>
                                    <button
                                        type="button"
                                        className="sa-btn sa-btn-primary"
                                        onClick={closeAddModal}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <h3 id="sa-modal-title" className="sa-modal-title">
                                    Add Attendee Manually — {activeEvent.label}
                                </h3>
                                <p className="sa-subtitle">
                                    For comped tickets, offline payments, or guests. No confirmation
                                    email is sent.
                                </p>
                                <form className="sa-form" onSubmit={handleAddAttendee}>
                                    <div className="sa-form-grid sa-form-grid-modal">
                                        <label className="sa-form-field">
                                            <span>Name *</span>
                                            <input
                                                type="text"
                                                value={form.name}
                                                onChange={setField('name')}
                                                placeholder="Full name"
                                                required
                                                autoFocus
                                            />
                                        </label>
                                        <label className="sa-form-field">
                                            <span>Email *</span>
                                            <input
                                                type="email"
                                                value={form.email}
                                                onChange={setField('email')}
                                                placeholder="name@example.com"
                                                required
                                            />
                                        </label>
                                        <label className="sa-form-field">
                                            <span>Ticket Type *</span>
                                            <select value={form.ticketType} onChange={setField('ticketType')}>
                                                <option value="creator">Creator</option>
                                                <option value="host">Host</option>
                                            </select>
                                        </label>
                                        <label className="sa-form-field">
                                            <span>Property Name{form.ticketType === 'host' ? '' : ' (optional)'}</span>
                                            <input
                                                type="text"
                                                value={form.propertyName}
                                                onChange={setField('propertyName')}
                                                placeholder="e.g. Little Mountain A-Frame"
                                            />
                                        </label>
                                        <label className="sa-form-field">
                                            <span>Social Handle (optional)</span>
                                            <input
                                                type="text"
                                                value={form.socialHandle}
                                                onChange={setField('socialHandle')}
                                                placeholder="@handle"
                                            />
                                        </label>
                                        {eventYear === 2027 && (
                                            <>
                                                <label className="sa-form-field sa-form-field-check">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.speakerInterest}
                                                        onChange={(e) =>
                                                            setForm((prev) => ({
                                                                ...prev,
                                                                speakerInterest: e.target.checked,
                                                            }))
                                                        }
                                                    />
                                                    <span>Interested in speaking</span>
                                                </label>
                                                {form.speakerInterest && (
                                                    <label className="sa-form-field sa-form-field-wide">
                                                        <span>Speaking Topic (optional)</span>
                                                        <textarea
                                                            rows={3}
                                                            maxLength={500}
                                                            value={form.speakerTopic}
                                                            onChange={setField('speakerTopic')}
                                                            placeholder="What would they like to speak about?"
                                                        />
                                                    </label>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    {formError && <div className="admin-error">{formError}</div>}
                                    <div className="sa-modal-actions">
                                        <button
                                            type="button"
                                            className="sa-btn sa-btn-secondary"
                                            onClick={closeAddModal}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </button>
                                        <button type="submit" className="sa-btn sa-btn-primary" disabled={saving}>
                                            <UserPlus size={14} />
                                            {saving ? 'Adding…' : 'Add Attendee'}
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}

            {importOpen && (
                <div className="sa-modal-overlay" onClick={closeImport}>
                    <div
                        className="sa-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="sa-import-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="sa-modal-close"
                            onClick={closeImport}
                            disabled={importing}
                            aria-label="Close"
                        >
                            <X size={18} />
                        </button>

                        {importResult ? (
                            <div className="sa-modal-success">
                                <CheckCircle2 size={44} className="sa-modal-success-icon" />
                                <h3 id="sa-import-title">Import complete</h3>
                                <p>
                                    <strong>{importResult.inserted}</strong> attendee{importResult.inserted === 1 ? '' : 's'} added to {activeEvent.label}.
                                    {importResult.duplicates?.length ? ` ${importResult.duplicates.length} already on the list were skipped.` : ''}
                                    {importResult.invalid?.length ? ` ${importResult.invalid.length} row${importResult.invalid.length === 1 ? '' : 's'} had no valid email.` : ''}
                                    {eventYear === 2026 ? ' Everyone on the 2026 list gets the attendee rate on Postaway Academy packages when they check out with that email.' : ''}
                                </p>
                                <div className="sa-modal-actions">
                                    <button type="button" className="sa-btn sa-btn-primary" onClick={closeImport}>
                                        Done
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <h3 id="sa-import-title" className="sa-modal-title">
                                    Import Attendees — {activeEvent.label}
                                </h3>
                                <p className="sa-subtitle">
                                    Upload an Eventbrite export (or any CSV with an email column). Names come from
                                    “Name” or “First Name” + “Last Name”; ticket type is read from a “Ticket Type”
                                    column when present (anything containing “host” becomes Host, otherwise Creator).
                                    Emails already on this event’s list are skipped, so re-importing is safe.
                                </p>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv,text/csv"
                                    style={{ display: 'none' }}
                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                />
                                <div
                                    className="sa-dropzone"
                                    onClick={() => fileInputRef.current?.click()}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                                >
                                    <Upload size={20} />
                                    <span>{importFileName ? importFileName : 'Choose a CSV file or drop it here'}</span>
                                </div>

                                {importing && !importPreview && <p className="sa-subtitle">Reading file…</p>}
                                {importError && <div className="admin-error">{importError}</div>}

                                {importPreview && (
                                    <div className="sa-import-preview">
                                        <div className="sa-stats sa-import-stats">
                                            <div className="sa-stat">
                                                <span className="sa-stat-value">{importPreview.inserted}</span>
                                                <span className="sa-stat-label">New</span>
                                            </div>
                                            <div className="sa-stat">
                                                <span className="sa-stat-value">{importPreview.duplicates?.length || 0}</span>
                                                <span className="sa-stat-label">Already listed</span>
                                            </div>
                                            <div className="sa-stat">
                                                <span className="sa-stat-value">{importPreview.invalid?.length || 0}</span>
                                                <span className="sa-stat-label">No valid email</span>
                                            </div>
                                        </div>
                                        {importPreview.preview?.length > 0 && (
                                            <div className="ca-table-wrap sa-import-table">
                                                <table className="ca-table">
                                                    <thead>
                                                        <tr><th>Name</th><th>Email</th><th>Type</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {importPreview.preview.map((r) => (
                                                            <tr key={r.email} className="ca-row" style={{ cursor: 'default' }}>
                                                                <td className="sa-cell-name">{r.name}</td>
                                                                <td>{r.email}</td>
                                                                <td>
                                                                    <span className={`sa-badge sa-badge-${r.ticketType}`}>
                                                                        {TICKET_LABELS[r.ticketType] || r.ticketType}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                                {importPreview.inserted > importPreview.preview.length && (
                                                    <p className="sa-subtitle">…and {importPreview.inserted - importPreview.preview.length} more.</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="sa-modal-actions">
                                    <button
                                        type="button"
                                        className="sa-btn sa-btn-secondary"
                                        onClick={closeImport}
                                        disabled={importing}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="sa-btn sa-btn-primary"
                                        onClick={commitImport}
                                        disabled={importing || !importPreview || !importPreview.inserted}
                                    >
                                        <Upload size={14} />
                                        {importing ? 'Importing…' : `Import ${importPreview?.inserted || 0} attendee${importPreview?.inserted === 1 ? '' : 's'}`}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default SummitAttendees;
